/* eslint-disable import/no-cycle */
import { useMutation, useQuery } from "@tanstack/react-query";
import { AddShiftMultiStatusResponse, AddShiftResultDtoStatusEnum } from "@bluecrew/finance-service-node-client";
import { formatPayrollReportsData } from "../../../containers/Payroll/PayrollReports/utils";
import {
  buildApproveShiftPayload,
  buildCreateIntercomDisputePayload,
  buildCreateRawJobPayload,
  buildPutOnJobPayload,
  buildUpdateHoursPayload,
  buildUpdatePayrollHoursStatusPayload,
  processAllWorkers,
  processTimesheetsShiftInfoApiV1,
  processCompanyPositions,
  buildUpdateHoursTimekeepingPayload,
  buildApproveTimekeepingPayload,
  buildAddShiftTimekeepingPayload,
} from "../../../containers/Payroll/payrollDataProcessors";
import {
  addShiftTimekeeping,
  approveShift,
  approveTimekeeping,
  createIntercomDispute,
  createRawJob,
  getAllWorkers,
  getCompanyPositions,
  getPayrollReportsRequest,
  getPayrollWeek,
  postCompanyPayrollRequestV1,
  postPayrollHoursRequest,
  putOnJob,
  undoPayrollAction,
  updateHoursTimekeeping,
  updatePayrollHours,
  updatePayrollHoursStatus,
} from "../payroll";
import {
  AddShiftRequest,
  AddShiftTimekeepingRequest,
  AddShiftPartialResult,
} from "../../../containers/Payroll/Timesheets/AddShift/AddShiftForm.types";
import QueryKeys from "./queryKeys";
import { queryClient } from "../../../queryClient";
import {
  ApproveShiftRequest,
  DisputeShiftRequest,
  ExcuseShiftRequest,
  ErrorResponse,
  PayrollAction,
  PayrollHoursStatus,
  RejectShiftRequest,
  ApproveTimekeepingRequest,
  DisputeTimekeepingRequest,
} from "../../../containers/Payroll/types/Payroll.types";
import { ShiftKey, TimesheetsShiftInfo } from "../../../containers/Payroll/types/Shift.types";
import { intercomGroup, IntercomGroupPayload, TimesheetMessagePayload } from "../intercom";
import { useGetCompanyShifts } from "./finance";
import { logError } from "../../../sentry";

export const genericErrorMessage = "An error occurred. Please, try again.";

export async function getMessageForError(
  error: ErrorResponse,
  messageField: "message" | "reason" = "message",
) {
  let errorMessage: string = genericErrorMessage;
  if (error.response?.json) {
    // Note: the existence of the method does not imply the response is JSON
    try {
      const data = await error.response.json();
      if (data[messageField]) {
        errorMessage = data[messageField]!;
      } else if (data.code) {
        errorMessage = data.code;
      } else {
        errorMessage = genericErrorMessage;
      }
    } catch {
      // The response is not JSON
      errorMessage = genericErrorMessage;
    }
  }
  return errorMessage;
}

export const useGetPayrollReportsQuery = (payrollWeek = new Date()) =>
  useQuery({
    queryKey: [...QueryKeys.Payroll_PayrollReports, payrollWeek],
    queryFn: () => getPayrollReportsRequest(payrollWeek),
    select: (data) => {
      const formattedData = formatPayrollReportsData(data);
      return formattedData;
    },
    // Add larger delay temporarily to avoid DB contention
    retryDelay: (attemptIndex) => Math.min(1000 * (Math.random() * 4 + 1) ** attemptIndex, 30000),
  });

export const usePostCompanyPayrollQueryV1 = (date: Date, companyId: string | undefined) =>
  useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [...QueryKeys.Payroll_CompanyPayroll, companyId, date.toISOString().split('T')[0]],
    queryFn: () => postCompanyPayrollRequestV1(companyId!, getPayrollWeek(date)),
    enabled: !!companyId,
    select: (data) => processTimesheetsShiftInfoApiV1(data),
  });

export const useSplitPostCompanyPayrollQuery = (
  date: Date,
  companyId: string | undefined,
  splitValue: string,
) => {
  const financeQuery = useGetCompanyShifts(date, companyId);
  const payrollQuery = usePostCompanyPayrollQueryV1(date, companyId);
  return splitValue === "on" ? financeQuery : payrollQuery;
};

export const usePostPayrollHoursQuery = (date: Date, companyId: string) =>
  useQuery({
    queryKey: [...QueryKeys.Payroll_PayrollHours, date, companyId],
    queryFn: () => postPayrollHoursRequest(companyId, getPayrollWeek(date)),
  });

export const useGetCompanyPositionsQuery = (companyId: string) =>
  useQuery({
    queryKey: [...QueryKeys.Payroll_CompanyPositions, companyId],
    queryFn: () => getCompanyPositions(companyId),
    select: (data) => processCompanyPositions(data.positions),
  });

export const useGetAllWorkersQuery = (companyId: string) =>
  useQuery({
    queryKey: [...QueryKeys.Payroll_GetAllWorkers, companyId],
    queryFn: () => getAllWorkers(companyId),
    select: (data) => processAllWorkers(data),
  });

export const useAddShiftMutation = (onSuccess: () => void, onError: (message: string) => void) =>
  useMutation({
    mutationKey: QueryKeys.Payroll_AddShift,
    mutationFn: async (request: AddShiftRequest) => {
      const createRawJobResponse = await createRawJob(buildCreateRawJobPayload(request));
      await putOnJob(buildPutOnJobPayload(request, createRawJobResponse.jobId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts });
      onSuccess();
    },
    onError: (error?: { message?: string }) => {
      const errorMessage = error?.message ? error.message : genericErrorMessage;
      onError(errorMessage);
    },
    retry: false,
  });

export const useAddShiftTimekeepingMutation = (
  onSuccess: () => void,
  onError: (message: string) => void,
  onPartialSuccess: (result: Array<AddShiftPartialResult>) => void,
) =>
  useMutation({
    mutationKey: QueryKeys.Payroll_AddShiftTimekeeping,
    mutationFn: (request: AddShiftTimekeepingRequest) =>
      addShiftTimekeeping(buildAddShiftTimekeepingPayload(request)),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts });
      if (response.status === 207) {
        const data = await getPartialSuccessResponse(response);
        if (data && data.results) {
          const successfulUsers = data.results
            .filter(result => result.status === AddShiftResultDtoStatusEnum.Success);
          const failedUsers = data.results
            .filter(result => result.status === AddShiftResultDtoStatusEnum.Error);

          if (failedUsers.length > 0 && successfulUsers.length > 0) {
            onPartialSuccess(data.results);
          } else if (failedUsers.length === 0) {
            onSuccess();
          } else {
            onError(failedUsers[0]?.errorMessage || "All users failed to be added");
          }
        } else {
          onSuccess();
        }
      } else {
        onSuccess();
      }
    },
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

async function getPartialSuccessResponse(response: Response): Promise<AddShiftMultiStatusResponse | null> {
  try {
    const data = await response.json();
    return (
      typeof data === "object" && data !== null && "results" in data && Array.isArray(data.results)
    ) ? data as AddShiftMultiStatusResponse : null;
  } catch {
    return null;
  }
}

const getPayrollActionKey = (shiftKey: ShiftKey, action: PayrollAction) => [
  ...getPayrollActionKeyPrefix(shiftKey),
  action,
];

export const getPayrollActionKeyPrefix = (shiftKey: ShiftKey) => [
  ...QueryKeys.Payroll_Action,
  ...Object.values(shiftKey),
];

export const useApproveShiftMutation = (shiftKey: ShiftKey, onError: (message: string) => void) =>
  useMutation({
    mutationKey: getPayrollActionKey(shiftKey, PayrollAction.APPROVE),
    mutationFn: (request: ApproveShiftRequest) => approveShift(buildApproveShiftPayload(request)),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts }),
        queryClient.invalidateQueries({ queryKey: QueryKeys.Payroll_CompanyPayroll }),
      ]),
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

export const useApproveTimekeepingMutation = (
  shiftKey: ShiftKey,
  onError: (message: string) => void,
) =>
  useMutation({
    mutationKey: getPayrollActionKey(shiftKey, PayrollAction.APPROVE_TIMEKEEPING),
    mutationFn: (request: ApproveTimekeepingRequest) =>
      approveTimekeeping(buildApproveTimekeepingPayload(request)),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts }),
        queryClient.invalidateQueries({ queryKey: QueryKeys.Payroll_CompanyPayroll }),
      ]),
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

export const useDisputeShiftMutation = (shiftKey: ShiftKey, onError: (message: string) => void) =>
  useMutation({
    mutationKey: getPayrollActionKey(shiftKey, PayrollAction.DISPUTE),
    mutationFn: async (request: DisputeShiftRequest) => {
      const hoursId = await updatePayrollHoursStatus(
        buildUpdatePayrollHoursStatusPayload(
          request,
          PayrollHoursStatus.DISPUTED,
          request.disputeInfo.reason.code,
        ),
      );
      const { clockIn, clockOut, breakDurationMins } = request.disputeInfo;
      const anyUserHoursEdits = clockIn || clockOut || Number.isFinite(breakDurationMins);
      if (anyUserHoursEdits) {
        try {
          await updatePayrollHours(
            buildUpdateHoursPayload(hoursId, clockIn, clockOut, breakDurationMins),
          );
        } catch (e) {
          // Move hours out of dispute if shift/time updates fail. This gives WP client the chance for another manual retry.
          await undoPayrollAction(hoursId).catch((undoErr) =>
            logError({
              error: undoErr instanceof Error ? undoErr : new Error(String(undoErr)),
              context: "useDisputeShiftMutation rollback",
            }),
          );
          // Surface the original failure (e.g. closed client edit window) to onError so the user sees the message.
          throw e;
        }
      }
      createIntercomDispute(buildCreateIntercomDisputePayload(request));
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts }),
        queryClient.invalidateQueries({ queryKey: QueryKeys.Payroll_CompanyPayroll }),
      ]),
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

export const useDisputeTimekeepingMutation = (
  shiftKey: ShiftKey,
  onError: (message: string) => void,
) =>
  useMutation({
    mutationKey: getPayrollActionKey(shiftKey, PayrollAction.DISPUTE_TIMEKEEPING),
    mutationFn: async (request: DisputeTimekeepingRequest) => {
      const hoursId = await updatePayrollHoursStatus(
        buildUpdatePayrollHoursStatusPayload(
          request,
          PayrollHoursStatus.DISPUTED,
          request.disputeInfoTimekeeping.reason.code,
        ),
      );
      const { shiftStart, shiftEnd, breaks } = request.disputeInfoTimekeeping;
      const anyUserHoursEdits = shiftStart || shiftEnd || breaks;
      if (anyUserHoursEdits) {
        try {
          await updateHoursTimekeeping(buildUpdateHoursTimekeepingPayload(hoursId, request));
        } catch (e) {
          // Move hours out of dispute if shift/time updates fail. This gives WP client the chance for another manual retry.
          await undoPayrollAction(hoursId).catch((undoErr) =>
            logError({
              error: undoErr instanceof Error ? undoErr : new Error(String(undoErr)),
              context: "useDisputeTimekeepingMutation rollback",
            }),
          );
          // Surface the original failure (e.g. closed client edit window) to onError so the user sees the message.
          throw e;
        }
      }
      createIntercomDispute(buildCreateIntercomDisputePayload(request));
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts }),
        queryClient.invalidateQueries({ queryKey: QueryKeys.Payroll_CompanyPayroll }),
      ]),
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

export const useRejectShiftMutation = (shiftKey: ShiftKey, onError: (message: string) => void) =>
  useMutation({
    mutationKey: getPayrollActionKey(shiftKey, PayrollAction.REJECT),
    mutationFn: (request: RejectShiftRequest) =>
      updatePayrollHoursStatus(
        buildUpdatePayrollHoursStatusPayload(request, PayrollHoursStatus.REJECTED, request.reason),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts }),
        queryClient.invalidateQueries({ queryKey: QueryKeys.Payroll_CompanyPayroll }),
      ]),
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

export const useExcuseShiftMutation = (shiftKey: ShiftKey, onError: (message: string) => void) =>
  useMutation({
    mutationKey: getPayrollActionKey(shiftKey, PayrollAction.Excuse),
    mutationFn: (request: ExcuseShiftRequest) =>
      updatePayrollHoursStatus(
        buildUpdatePayrollHoursStatusPayload(request, PayrollHoursStatus.DAY_OFF, request.reason),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts }),
        queryClient.invalidateQueries({ queryKey: QueryKeys.Payroll_CompanyPayroll }),
      ]),
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

export const useUndoPayrollActionMutation = (
  shiftKey: ShiftKey,
  onError: (message: string) => void,
) =>
  useMutation({
    mutationKey: getPayrollActionKey(shiftKey, PayrollAction.UNDO),
    mutationFn: (hoursId: number) => undoPayrollAction(hoursId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: QueryKeys.Finance_GetCompanyShifts }),
        queryClient.invalidateQueries({ queryKey: QueryKeys.Payroll_CompanyPayroll }),
      ]),
    onError: async (error: ErrorResponse) => onError(await getMessageForError(error)),
    retry: false,
  });

export const usePayrollSendMessage = (
  onSuccess: () => void,
  onError: (message: string) => void,
) => {
  return useMutation(
    QueryKeys.Payroll_SendMessage,
    ({
      userId,
      message,
      shiftInfo,
    }: {
      userId: string;
      message: string;
      shiftInfo?: TimesheetsShiftInfo;
    }) => {
      if (shiftInfo) {
        const timesheetMessagePayload: TimesheetMessagePayload = {
          externalCompanyId: shiftInfo.company.externalId,
          week: getPayrollWeek(shiftInfo.job.start),
          externalJobId: shiftInfo.job.externalId,
          timeSegmentAbsoluteId: shiftInfo.job.shiftIndex,
        };

        const payload: IntercomGroupPayload = {
          fromCompany: false,
          // api-v1 requires this to be arrays of the same length. For now we use this just for a single CM, so do this array-hack
          userIds: [userId],
          messages: [message],
          timesheetMessages: [timesheetMessagePayload],
        };

        return intercomGroup(payload);
      }

      const payload: IntercomGroupPayload = {
        fromCompany: false,
        // api-v1 requires this to be arrays of the same length. For now we use this just for a single CM, so do this array-hack
        userIds: [userId],
        messages: [message],
      };

      return intercomGroup(payload);
    },
    {
      onSuccess,
      onError: async (error: ErrorResponse) => onError(await getMessageForError(error, "reason")),
    },
  );
};
